import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './Products.css';
import Footer from './Footer';
import ContactOpticalCRM from './ContactOpticalCRM';
import SEO from './SEO';

const Products = () => {
  const [isSectionVisible, setIsSectionVisible] = useState(false);
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const imageWrapperRef = useRef(null);

  useEffect(() => {
    const sectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsSectionVisible(true);
            sectionObserver.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    if (sectionRef.current) {
      sectionObserver.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        sectionObserver.unobserve(sectionRef.current);
      }
    };
  }, []);

  const features = [
    {
      id: 1,
      title: 'AI-Powered CRM',
      description: 'Leverage artificial intelligence to automate customer management, track interactions, and deliver personalized experiences for your optical business.',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: '#667eea'
    },
    {
      id: 2,
      title: 'Complete Business Management',
      description: 'Inventory, sales and billing, customers, suppliers, credit payments, and more—all on one unified platform designed for optical businesses.',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      color: '#f5576c'
    },
    {
      id: 3,
      title: 'Real-Time Analytics',
      description: 'Make data-driven decisions with powerful analytics and insights that help you understand your business performance and customer behavior.',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      color: '#4facfe'
    }
  ];

  const products = [
    {
      id: 1,
      name: 'AI-Powered Solutions',
      description: 'Leverage artificial intelligence to automate processes, make data-driven decisions, and enhance customer experiences.',
      image: '/images/resource/offer-2.jpg',
      category: 'Enterprise Software'
    },
    {
      id: 2,
      name: 'IoT Development Platform',
      description: 'Connect and manage your devices with our comprehensive IoT platform for smart business operations.',
      image: '/images/resource/offer-3.jpg',
      category: 'IoT Solutions'
    },
    {
      id: 3,
      name: 'Mobile Applications',
      description: 'Build high-performance mobile apps for iOS and Android with seamless user experiences.',
      image: '/images/resource/offer-4.jpg',
      category: 'Mobile Development'
    },
    {
      id: 4,
      name: 'Web Platforms',
      description: 'Create secure, scalable web applications with modern technologies and stunning designs.',
      image: '/images/resource/offer-1.jpg',
      category: 'Web Development'
    },
    {
      id: 5,
      name: 'Cloud Solutions',
      description: 'Migrate to the cloud with our comprehensive cloud infrastructure and management services.',
      image: '/images/main-slider/iot.jpg',
      category: 'Cloud Services'
    },
    {
      id: 6,
      name: 'Business Analytics',
      description: 'Make informed decisions with powerful analytics and reporting tools that provide real-time insights.',
      image: '/images/resource/about-1.jpg',
      category: 'Analytics'
    }
  ];

  const capabilities = [
    {
      title: 'Never Run Out of Resources',
      description: 'Have the right resources at the right time with automated management and alert systems for optimal performance.',
      image: '/images/resource/about-2.jpg'
    },
    {
      title: 'Complete Business Management',
      description: 'Manage your entire business operations from a single platform. Track every aspect and keep tabs on your business health.',
      image: '/images/resource/about-3.png'
    },
    {
      title: 'Powerful Analytics',
      description: 'Know what\'s happening at every stage of your business and improve it through comprehensive reports and insights.',
      image: '/images/resource/about-6.jpg'
    }
  ];

  return (
    <>
      <SEO 
        title="Optical CRM - Complete Solution for Eyewear Businesses"
        description="The most powerful CRM for optical and eyewear businesses. Manage customers, prescriptions, inventory, frame catalogs, lens orders, POS, and sales analytics all in one platform."
        keywords="optical CRM, eyewear CRM, optician software, optical shop management, prescription management, lens ordering system, optical inventory, eyewear business software"
        url="/optical-crm"
        product={{
          name: "Optical CRM",
          description: "Complete CRM solution for optical and eyewear businesses with customer management, prescription tracking, inventory control, and POS.",
          operatingSystem: "Web, iOS, Android",
          features: "Customer Management, Prescription Tracking, Inventory Control, POS System, Sales Analytics, Multi-store Support"
        }}
      />
      <section className="products-hero">
        <div className="container">
          <div className="products-hero-content">
            <h4>Powering India’s Optical CRM with AI</h4>
            <h1>Smart CRM Designed for Optical Businesses.</h1>
            <p className="products-hero-subtitle">
            Run your business smarter with Fractallogy.
            Innovate faster, manage operations seamlessly, and grow digitally — all on one unified platform.
            </p>
            <div className="products-hero-cta">
              <button 
                className="cta-button primary" 
                onClick={() => window.dispatchEvent(new CustomEvent('openContactForm'))}
              >
                CONTACT SALES
              </button>
              <button
                className="cta-button secondary"
                onClick={() => window.open('https://crm.fractallogy.com/', '_blank')}
              >
                Get a Demo
              </button>
            </div>
          </div>
        </div>
      </section>
      <section className="products-intro">
        <div className="container">
          <div className="products-intro-content">
            <h2>Everything you need to run your optical business efficiently.</h2>
            <div className="products-features-grid">
              {features.map((feature) => {
                // Convert hex color to RGB for glow effect
                const hex = feature.color.replace('#', '');
                const r = parseInt(hex.substr(0, 2), 16);
                const g = parseInt(hex.substr(2, 2), 16);
                const b = parseInt(hex.substr(4, 2), 16);
                
                return (
                  <div 
                    key={feature.id} 
                    className="products-feature-card"
                    style={{
                      '--glow-color': feature.color,
                      '--glow-rgb': `${r}, ${g}, ${b}`
                    }}
                  >
                    <h3 style={{ color: '#000000' }}>{feature.title}</h3>
                    <p>{feature.description}</p>
                    <div className="feature-card-accent" style={{ background: feature.gradient }}></div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
      <section className="pos-showcase">
        <div className="pos-showcase-header">
          <h1>See the Future of Retail Management.</h1>
          <p className="showcase-description">
            Experience how our intelligent retail management system transforms everyday business operations with powerful AI-driven insights, seamless inventory tracking, and customer-first solutions.
          </p>
        </div>
        <div className="pos-showcase-container">
          <div className="pos-showcase-background">
            <img 
              src="/images/topimage.png" 
              alt="Retail store owner managing business" 
            />
          </div>
          <div className="pos-showcase-device">
            <div className="device-frame">
              <div className="device-header">
                <div className="device-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
              <div className="device-screen">
                <video 
                  autoPlay 
                  loop 
                  muted 
                  playsInline
                >
                  <source src="https://websitestorage.blr1.cdn.digitaloceanspaces.com/fractallogy_website/crm.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="communication-section">
        <div className="container">
          <div className="communication-header">
            <div className="communication-icons">
              <svg className="chat-icon left" viewBox="0 0 64 64" fill="none">
                <path d="M32 4C16.536 4 4 15.64 4 30c0 5.6 1.8 10.8 4.88 15.04L4 56l12.4-3.92C20.8 54.4 26.24 56 32 56c15.464 0 28-11.64 28-26S47.464 4 32 4z" fill="#FCCD04" stroke="#2c3e50" strokeWidth="2"/>
                <circle cx="20" cy="30" r="4" fill="#2c3e50"/>
                <circle cx="32" cy="30" r="4" fill="#2c3e50"/>
                <circle cx="44" cy="30" r="4" fill="#2c3e50"/>
              </svg>
            </div>
            <h2 className="communication-title">
              <span className="script-text">Talk, share, and collaborate instantly</span>
              <br />
              <span className="script-text">with chat, audio, video, and documents.</span>
            </h2>
            <div className="communication-icons">
              <svg className="chat-icon right" viewBox="0 0 64 64" fill="none">
                <path d="M32 4C16.536 4 4 15.64 4 30c0 5.6 1.8 10.8 4.88 15.04L4 56l12.4-3.92C20.8 54.4 26.24 56 32 56c15.464 0 28-11.64 28-26S47.464 4 32 4z" fill="#FCCD04" stroke="#2c3e50" strokeWidth="2"/>
                <circle cx="20" cy="30" r="4" fill="#2c3e50"/>
                <circle cx="32" cy="30" r="4" fill="#2c3e50"/>
              </svg>
            </div>
          </div>
          <p className="communication-description">
            <strong>Team chat for CRM users.</strong> Group conversations, share documents, store queries, product images, and discuss customers and inventory—all in one place.
          </p>
          <div className="communication-showcase-container">
            <div className="abstract-overlay"></div>
            <div className="communication-showcase">
              <div className="communication-video-wrapper">
                <div className="communication-browser-header">
                  <div className="communication-browser-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <div className="communication-browser-address">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                    </svg>
                    app.fractallogy.com/chat
                  </div>
                </div>
                <video 
                  autoPlay 
                  loop 
                  muted 
                  playsInline
                >
                  <source src="https://websitestorage.blr1.cdn.digitaloceanspaces.com/fractallogy_website/chat.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section ref={sectionRef} className="vendor-ledger-section">
        <div className="container">
          <div 
            ref={headerRef}
            className={`vendor-ledger-header ${isSectionVisible ? 'header-visible' : ''}`}
          >
            <h2 className="vendor-ledger-title">Vendor Ledger Management</h2>
            <p className="vendor-ledger-description">
              Track and manage all your vendor transactions, payments, and outstanding balances in one comprehensive ledger system.
            </p>
          </div>
        </div>
        <div className={`vendor-ledger-showcase ${isSectionVisible ? 'animate-background' : ''}`}>
          {/* Background scrolling images */}
          <div className="vendor-ledger-background-images">
            <div className="bg-image bg-image-1">
              <div className="bg-card expense-report-card">
                <div className="bg-card-header">Expense Report</div>
                <div className="bg-card-content">
                  <div className="bg-card-amount">Total $200.00</div>
                  <div className="bg-card-tag pending">Pending Approval</div>
                  <div className="bg-card-flow">
                    <div className="flow-avatar"></div>
                    <div className="flow-avatar approved"></div>
                    <div className="flow-avatar"></div>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-image bg-image-2">
              <div className="bg-card payment-mode-card">
                <div className="bg-card-header">Payment Mode</div>
                <div className="bg-card-content">
                  <div className="bg-card-item">
                    <span className="bg-icon">💳</span>
                    <span>Direct Deposit</span>
                  </div>
                  <div className="bg-card-item">
                    <span className="bg-icon">🏦</span>
                    <span>Bank Transfer</span>
                  </div>
                  <div className="bg-card-item">
                    <span className="bg-icon">📄</span>
                    <span>Cheque</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-image bg-image-3">
              <div className="bg-card mobile-card">
                <div className="bg-card-header">EXPENSE</div>
                <div className="bg-card-content mobile-content">
                  <div className="mobile-merchant">ZYLKER CAFE</div>
                  <div className="mobile-receipt"></div>
                </div>
              </div>
            </div>
            <div className="bg-image bg-image-4">
              <div className="bg-card payroll-card">
                <div className="bg-card-header">PAYROLL</div>
                <div className="bg-card-content payroll-content">
                  <div className="payroll-avatar"></div>
                  <div className="payroll-text">Review Documents</div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Main centered image */}
          <div 
            ref={imageWrapperRef}
            className={`vendor-ledger-image-wrapper ${isSectionVisible ? 'image-scrolled' : ''}`}
          >
            <img 
              src="/images/vendor_ledger.jpg" 
              alt="Vendor Ledger Management System" 
              className="vendor-ledger-image"
            />
          </div>
        </div>
      </section>

      <ContactOpticalCRM />
      <Footer />
    </>
  );
};

export default Products;

